"use client";

import { useEffect, useRef } from "react";

import { CcBeforeAfter } from "@/components/services/shared/CcBeforeAfter";
import { CcButton } from "@/components/ui/CcButton";
import { CcSanjayaEyebrow } from "@/components/ui/CcSanjayaEyebrow";
import { devSectionAttr } from "@/lib/dev/section-attr";
import { useCcPrefersReducedMotion } from "@/lib/use-cc-prefers-reduced-motion";

import type { WebValueDemoCopy } from "./web-value-demo-copy";

type CcWebShowcaseProps = {
  copy: WebValueDemoCopy["showcase"];
};

/** Share of "before" shown at the start, so the new site leads. */
const START_POSITION = 25;

/**
 * Real before/after of client websites as a stack of large rounded cards:
 * each one sticks below the nav while the next slides up over it, and the
 * card underneath settles back (smaller, dimmer). The divider starts at 25 %
 * so most of the new site shows from the start. No made-up results.
 */
export function CcWebShowcase({ copy }: CcWebShowcaseProps) {
  const listRef = useRef<HTMLOListElement>(null);
  const reduced = useCcPrefersReducedMotion();

  // Each card's --q: how far the next card has covered it (0 → 1).
  useEffect(() => {
    const list = listRef.current;
    if (!list || reduced) return;
    const cards = Array.from(list.children) as HTMLElement[];
    let frame = 0;
    const update = () => {
      frame = 0;
      cards.forEach((card, index) => {
        const next = cards[index + 1];
        if (!next) return;
        const stick = card.getBoundingClientRect().top;
        const nextTop = next.getBoundingClientRect().top;
        const span = card.offsetHeight;
        const q = 1 - (nextTop - stick) / span;
        card.style.setProperty("--q", Math.min(1, Math.max(0, q)).toFixed(3));
      });
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      cancelAnimationFrame(frame);
    };
  }, [reduced]);

  if (!copy.cases.length) return null;
  const total = String(copy.cases.length).padStart(2, "0");

  return (
    <section
      id="kundcase"
      className="cc-section cc-wsc"
      aria-labelledby="cc-wsc-title"
      data-cc-nav="light"
      {...devSectionAttr("web-showcase")}
    >
      <div className="cc-container">
        <header className="cc-wsc__head">
          <div>
            <CcSanjayaEyebrow as="p" theme="on-subtle">
              {copy.eyebrow}
            </CcSanjayaEyebrow>
            <h2 id="cc-wsc-title" className="cc-wsc__title">
              {copy.sectionTitle}
            </h2>
          </div>
          <p className="cc-wsc__lead">{copy.lead}</p>
        </header>

        <ol ref={listRef} className="cc-wsc__list">
          {copy.cases.map((item, index) => (
            <li
              key={item.id}
              className="cc-wsc__card"
              style={{ ["--i" as string]: index }}
            >
              <article aria-labelledby={`cc-wsc-${item.id}`}>
                <div className="cc-wsc__bar">
                  <div className="cc-wsc__name">
                    <span className="cc-wsc__count">
                      {String(index + 1).padStart(2, "0")} / {total}
                    </span>
                    <h3 id={`cc-wsc-${item.id}`}>{item.name}</h3>
                  </div>
                  <p className="cc-wsc__card-title">{item.title}</p>
                  <ul className="cc-wsc__points">
                    {item.points.map((point) => (
                      <li key={point}>{point}</li>
                    ))}
                  </ul>
                </div>

                <div
                  className="cc-wsc__media"
                  style={{ aspectRatio: `${item.width} / ${item.height}` }}
                >
                  <CcBeforeAfter
                    fillFrame
                    initialPosition={START_POSITION}
                    before={item.before}
                    after={item.after}
                    afterVideo={item.afterVideo}
                    afterVideoStart={item.afterVideoStart}
                    width={item.width}
                    height={item.height}
                    beforeFit={item.compareFit?.before}
                    afterFit={item.compareFit?.after}
                    beforeLabel={copy.beforeLabel}
                    afterLabel={copy.afterLabel}
                    sliderLabel={`${copy.sliderLabel}: ${item.name}`}
                    sizes="(max-width: 1280px) 100vw, 1240px"
                    quality={85}
                  />
                </div>
              </article>
            </li>
          ))}
        </ol>

        <div className="cc-wsc__foot">
          <p className="cc-wsc__hint">{copy.hint}</p>
          <CcButton
            bookCallPopup
            bookCallVariant="default"
            label={copy.button}
            expand
            expandCompact
          />
        </div>
      </div>
    </section>
  );
}
